import { trackCompositionTextEdited } from '@src/analytics/EventTracker';
import { useCompositionQuickEditOpenedTracking } from '@src/analytics/hooks/useCompositionQuickEditOpenedTracking';
import { DubCoverageNotice } from '@src/components/aiVoices/DubCoverageNotice';
import DubGenerateModal from '@src/components/aiVoices/DubGenerateModal';
import { DubLevelRow } from '@src/components/aiVoices/DubLevelRow';
import { useCreatorStudioDubGeneration } from '@src/components/aiVoices/useCreatorStudioDubGeneration';
import { useRemoveDub } from '@src/components/aiVoices/useRemoveDub';
import { useVoiceActivation } from '@src/components/aiVoices/useVoiceActivation';
import { ConfirmModal } from '@src/components/common/ConfirmModal';
import { usePlayerSettingsSlot } from '@src/components/common/playerSettingsSlot';
import { ResizeBorderVertical } from '@src/components/common/ResizeBorder';
import { TOP_LAYER_Z } from '@src/components/common/topLayerZ';
import { CLIP_PREVIEW_Z } from '@src/components/content-components/clipPreviewZ';
import { CompositionCanvasActionMenu } from '@src/components/content-components/CompositionCanvasActionMenu';
import { useCompositionPinDispatchers } from '@src/components/content-components/hooks/useCompositionPinDispatchers';
import { OverlayGraphicPickerModal } from '@src/components/content-components/OverlayGraphicPickerModal';
import { CompositionProvider } from '@src/components/sidepanel/CompositionContext';
import { buildAITextPopoverContext } from '@src/components/sidepanel/CompositionSettings/controls/aiTextPopoverContext';
import { useCreatorStudioAITextGeneration } from '@src/components/sidepanel/CompositionSettings/controls/useCreatorStudioAITextGeneration';
import { CreatorStudioTranslationReview } from '@src/components/sidepanel/CreatorStudioTranslationReview';
import { CaptionStalenessNotice } from '@src/components/transcript/CaptionStalenessNotice';
import {
  CompositionQuickEditPopover,
  formatHeaderLabelFromMarker,
  type RetranslateCompositionDispatch,
} from '@src/components/transcript/CompositionQuickEditPopover';
import {
  type ClipTimelineMarkers,
  type TimelineMarker,
  useTimelineMarkers,
} from '@src/components/transcript/hooks/useTimelineMarkers';
import { PlayerAudioPickers } from '@src/components/transcript/PlayerAudioPickers';
import { Transcript } from '@src/components/transcript/Transcript';
import { TranscriptOverflowExtrasProvider } from '@src/components/transcript/TranscriptOverflowExtras';
import { TranslationGenerateModal } from '@src/components/transcript/TranslationGenerateModal';
import {
  LANGUAGE_MENU_HELP_TOOLTIP_ID,
  TranslationToggle,
} from '@src/components/transcript/TranslationToggle';
import { useTranslationGenerateModalController } from '@src/components/transcript/useTranslationGenerateModalController';
import useAddMediaToTimeline from '@src/hooks/useAddMediaToTimeline';
import { useCaptionsLanguageApplier } from '@src/hooks/useCaptionsLanguageApplier';
import { useLanguageRemovalFallback } from '@src/hooks/useLanguageRemovalFallback';
import { computeOpenClipInContentStudioSearch } from '@src/hooks/useNavigateBackToContentStudio';
import { useTranscriptLayoutResize } from '@src/hooks/useTranscriptLayoutResize';
import { useTranslationStoreHydration } from '@src/hooks/useTranslationStoreHydration';
import { useUpdateSearchParams } from '@src/hooks/useUpdateSearchParams';
import { saveClipFromTranscript } from '@src/services/AIClipProducer';
import SaveStoryAndVideoService, {
  createVideoCreatorSaveAdapter,
} from '@src/services/SaveStoryAndVideoService';
import SavePullQuoteService from '@src/services/transcript/SavePullQuoteService';
import {
  useDatoClient,
  useEditor,
  useKaraokeStore,
  useLayoutStore,
  useTranscription,
  useTranslationStore,
  useUserIdentity,
  useVideoSource,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { Studio } from '@src/types.ts/general';
import {
  CAPTIONS_LANGUAGE_CHANGED_EVENT,
  type CaptionsLanguageChangedDetail,
  isOriginalLang,
  ORIGINAL_LANGUAGE,
} from '@src/utility/captionLanguage';
import { extractErrorInfo } from '@src/utility/error';
import {
  deleteElementMetaKeys,
  upsertElementMetaObject,
} from '@src/utility/extraElementData';
import { formatLanguageCaption } from '@src/utility/languageCaption';
import { recordLastStoryLanguage } from '@src/utility/lastStoryLanguage';
import { LogManager } from '@src/utility/Logger';
import { removeOverlayAndPersist } from '@src/utility/overlayDeletion';
import { addOverlayFromTranscript } from '@src/utility/overlayInsertion';
import {
  applyPlacementEdgeOverrides,
  readElementTimelineRange,
} from '@src/utility/placementOverrides';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import {
  computeMarkerQuickEditFields,
  resolveQuickEditTargetId,
} from '@src/videoTranscriptionProcessor/translation/computeMarkerQuickEditFields';
import { extractTranslatableElements } from '@src/videoTranscriptionProcessor/translation/extractTranslatableElements';
import { getTranslatableSourceElements } from '@src/videoTranscriptionProcessor/translation/getTranslatableSourceElements';
import { routeRetranslateTargetId } from '@src/videoTranscriptionProcessor/translation/routeRetranslateTargetId';
import { observer } from 'mobx-react-lite';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { DraggableCore } from 'react-draggable';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import type { CompositionTemplate } from '@src/components/sidepanel/CompositionSettings/types';
import type { ReviewMode } from '@src/components/transcript/TranslationReviewHeader';
import type { HighlightHoverInfo } from '@src/hooks/useHighlightHoverPopover';
import type { OverlayTranscriptRange } from '@src/utility/overlayInsertion';

const logger = LogManager.getLogger('component:LeftSidePanel');

export const LeftSidePanel: React.FC<{}> = observer(() => {
  const { isDragging, handleMouseMove, handleMouseDown, handleMouseUp } =
    useTranscriptLayoutResize();

  const layoutStore = useLayoutStore();
  const videoCreator = useVideoCreatorStore();
  const datoClientStore = useDatoClient();
  const editorStore = useEditor();
  const userIdentity = useUserIdentity();
  const videoSourceStore = useVideoSource();
  const translationStore = useTranslationStore();
  const transcriptionStore = useTranscription();
  const {
    creatorStudioTranscriptAddPhoto,
    enableTranscriptOverlayAdd,
    enableTranscriptOverlayRangeAdjust,
    enableTranslationReview,
    enableCompositionCanvasQuickEdit,
    enableAiVoices,
  } = useFlagsCombination(userIdentity.currentRole);
  const { transcriptWidth, transcriptMinWidth, transcriptMaxWidth } =
    layoutStore;

  // Per-view language selection — null = Original. Persisted in the URL
  // (`?lang=es-MX`) so a page refresh restores the selection. Each surface
  // (Creator Studio, Content Studio's T&S tab, redline, captions) still
  // holds its own independent selection per the decoupled per-view
  // architecture (Epic 1) — URL is scoped to the studio route.
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const selectedLanguage = searchParams.get('lang');
  const updateSearchParams = useUpdateSearchParams();
  const setUrlLanguage = useCallback(
    (lang: string | null) => {
      updateSearchParams(params => {
        if (lang) params.set('lang', lang);
        else params.delete('lang');
      });
    },
    [updateSearchParams],
  );

  // PLAT-9990: the left transcript language picker drives the same
  // editor karaoke config as the captions panel toggle does, so the
  // composition translation projection (PLAT-9966) picks up the new
  // active language regardless of which sidepanel is currently open.
  // Earlier behaviour (PLAT-9621) only propagated when the captions
  // panel was active; that decoupling left compositions untranslated
  // when the user changed language from any other panel. Common
  // apply-and-propagate logic lives in `useCaptionsLanguageApplier`.
  const editor = videoCreator.rootStore?.editorStore?.editor;
  const applyCaptionsLanguageToEditor = useCaptionsLanguageApplier(editor);
  const setSelectedLanguage = useCallback(
    (lang: string | null) => {
      setUrlLanguage(lang);
      // PLAT-11898 — a pick is what the story remembers (Original clears it).
      recordLastStoryLanguage(videoCreator.storyId, lang);
      // PLAT-10816 — a user's own pick, so it gets an undo entry.
      applyCaptionsLanguageToEditor(lang, { createUndoRedoEntry: true });
    },
    [setUrlLanguage, applyCaptionsLanguageToEditor, videoCreator.storyId],
  );

  // PLAT-11898 — Transcript's captions-view sync: the captions track is in a
  // different language than `?lang=`. The URL owns the caption language (DS8),
  // so the track follows it, loading its saved variant or producing one, as
  // the Captions panel's mount sync already does. Not a pick, so no memory and
  // no undo entry.
  const followUrlLanguage = useCallback(
    () => applyCaptionsLanguageToEditor(selectedLanguage),
    [applyCaptionsLanguageToEditor, selectedLanguage],
  );

  // PLAT-11898 — the same rule in every view, once per entry: an in-app return
  // can bring back captions left in another language (it changed in Content
  // Studio), under a transcript already in `?lang=`'s. Load-only — when the
  // URL's language has no saved captions they stay as they are rather than be
  // produced just by opening the page; Captions view produces them on demand.
  const karaokeStore = useKaraokeStore();
  const hasCaptionsTrack = karaokeStore.hasKaraokeLines;
  const captionsReconciledRef = useRef(false);
  useEffect(() => {
    if (captionsReconciledRef.current || !editor || !hasCaptionsTrack) return;
    captionsReconciledRef.current = true;
    if (karaokeStore.hasCaptionsIn(selectedLanguage ?? ORIGINAL_LANGUAGE)) {
      // A no-op when the captions are already in that language.
      applyCaptionsLanguageToEditor(selectedLanguage);
    }
  }, [
    editor,
    hasCaptionsTrack,
    karaokeStore,
    selectedLanguage,
    applyCaptionsLanguageToEditor,
  ]);

  // PLAT-9621: when the captions panel toggle changes the language,
  // mirror the selection into the transcript view's URL param. The
  // captions panel only fires this while the captions sidebar is active,
  // so the captions-only sync constraint is satisfied on this end too.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<CaptionsLanguageChangedDetail>).detail;
      setUrlLanguage(detail?.language ?? null);
      // PLAT-11898 — the captions panel's pick is a pick too.
      recordLastStoryLanguage(videoCreator.storyId, detail?.language ?? null);
    };
    window.addEventListener(CAPTIONS_LANGUAGE_CHANGED_EVENT, handler);
    return () => {
      window.removeEventListener(CAPTIONS_LANGUAGE_CHANGED_EVENT, handler);
    };
  }, [setUrlLanguage, videoCreator.storyId]);

  const generateModal = useTranslationGenerateModalController();

  // PLAT-9700: when the active translation is removed (from this toggle
  // or any other surface), fall back to Original so the transcript view
  // doesn't render an empty translated state.
  useLanguageRemovalFallback(selectedLanguage, () => setUrlLanguage(null));

  // Centralised hydration for the shared TranslationStore. Every surface
  // that renders a translation toggle pipes through this hook so story
  // ID bridging, supported languages, S3 discovery, and editorial
  // context all stay consistent.
  const supportedLanguages = useTranslationStoreHydration();

  // PLAT-11555 — this screen's dub controls: the Voice/music positions, the
  // timeline's gap sum, and the two runs it can start from that sum. Every
  // decision lives in the hook; this screen only binds it to the toggle and the
  // popover.
  // PLAT-11877 — Translation Review / Dubbing Review over this screen, the
  // same workspace the clip preview opens. Which job it opens on is set by
  // the control that opened it (pencil, microphone, a "ready" toast).
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewMode, setReviewMode] = useState<ReviewMode>('translation');
  const openReview = useCallback(
    (language: string, mode: ReviewMode) => {
      setSelectedLanguage(language);
      setReviewMode(mode);
      setReviewOpen(true);
    },
    [setSelectedLanguage],
  );
  const dubGeneration = useCreatorStudioDubGeneration({
    selectedLanguage,
    onReviewDub: enableTranslationReview
      ? language => openReview(language, 'dubbing')
      : undefined,
  });
  const { dubbingEnabled } = useVoiceActivation();
  const dubEntryEnabled = !!enableAiVoices && dubbingEnabled;
  // "Remove Dub" from a dubbed row's menu: the story's dub for that language,
  // as the clip preview offers it (PLAT-11663). Afterwards the dubbed list is
  // re-read and, if the timeline was playing that dub, the voice falls back.
  const captionForLanguage = useCallback(
    (code: string) =>
      supportedLanguages.find(l => l.value === code)?.caption ??
      formatLanguageCaption(code),
    [supportedLanguages],
  );
  const dubCompositionRef = useRef(dubGeneration.composition);
  dubCompositionRef.current = dubGeneration.composition;
  const removeDub = useRemoveDub({
    storyId: videoCreator.storyId,
    repository: datoClientStore.dubbingRepository,
    captionForLanguage,
    showToast: useCallback(
      (toast: { state: 'success' | 'error'; message: string }) =>
        videoCreator.showToast(toast),
      [videoCreator],
    ),
    onRemoved: useCallback((language: string) => {
      const composition = dubCompositionRef.current;
      composition.refreshDubbedLanguages?.();
      if (composition.appliedDubLanguage === language) {
        composition.onAudioModeChange?.('original');
      }
    }, []),
  });

  const saveService = useMemo(
    () =>
      new SaveStoryAndVideoService({
        videoCreator: createVideoCreatorSaveAdapter(videoCreator),
      }),
    [videoCreator],
  );

  const savePullQuoteService = useMemo(
    () => new SavePullQuoteService({ datoClientStore }),
    [datoClientStore],
  );

  const clipSaveService = useMemo(
    () => ({
      saveClip: async (input: {
        startIndex: number;
        endIndex: number;
        relevantElements?: any[];
      }) =>
        saveClipFromTranscript(videoCreator, {
          theme: 'social clip',
          transcriptPosition: {
            startIndex: input.startIndex,
            endIndex: input.endIndex,
            relevantElements: input.relevantElements,
          },
        }),
    }),
    [videoCreator],
  );

  const { addMediaToTimeline } = useAddMediaToTimeline({
    editorStore,
    videoCreator,
  });

  // PLAT-11752 — the Save as Clip toast's "Open Clip" action. Creator Studio has
  // no clip preview of its own, so it hands the new clip to Content Studio as a
  // `?clipId` deep link and lets `resolveClipPreviewSync` open the modal there.
  // Same link contract as the Back button: the Creator-only params are dropped
  // and the edited language rides along as `clipLang`.
  const handleOpenSavedClip = useCallback(
    (clipId: string) => {
      navigate({
        pathname: `/${Studio.content}`,
        search: computeOpenClipInContentStudioSearch(
          searchParams,
          clipId,
          videoCreator.story?.otherVideos,
        ).toString(),
      });
    },
    [navigate, searchParams, videoCreator],
  );

  const mediaService = useMemo(
    () => ({
      addPhotoToTimeline: async (input: {
        item: {
          id: string;
          src: string;
          description: string;
          aspectRatio: number;
          alt: string;
          type: 'artifact' | 'stock' | 'ai';
        };
        time: number;
        duration: number;
        track: number;
        useComposition: boolean;
        id: string;
      }) => {
        await addMediaToTimeline(input.item, 'photo', {
          time: input.time,
          duration: input.duration,
          track: input.track,
          useComposition: input.useComposition,
          id: input.id,
        });
      },
    }),
    [addMediaToTimeline],
  );

  // PLAT-10120 — overlay underline in the Creator Studio transcript. Markers
  // are sourced from `editorStore.tracksMap` (the @computed gated on
  // `timelineVersion`) rather than the non-observable `timeline.allElements`,
  // so trimming an overlay's start/end time re-derives the marker times and
  // the underline follows. Reading `tracksMap` here also establishes the MobX
  // dependency that re-renders this panel on timeline mutations.
  const overlayMarkerEditorStore = useMemo(
    () => ({
      editor: {
        timeline: {
          allElements: Array.from(editorStore.tracksMap.values()).flat(),
        },
      },
      transcriptElements: editorStore.transcriptionElements,
    }),
    [editorStore.tracksMap, editorStore.transcriptionElements],
  );
  const timelineMarkers = useTimelineMarkers(
    videoCreator,
    overlayMarkerEditorStore,
  );
  // Overlays only — intro / name / end cards keep their existing affordance
  // (no chips), so the other marker buckets are passed empty. The overlays
  // bucket drives the navy underline via `Transcript`'s overlay-range logic.
  const overlayOnlyMarkers = useMemo<ClipTimelineMarkers>(
    () => ({
      intro: [],
      end: [],
      nametagsByIndex: new Map(),
      overlays: timelineMarkers.overlays,
    }),
    [timelineMarkers.overlays],
  );
  // ── PLAT-10120: overlay hover edit pill + quick-edit popover ──────────
  // Hovering an underlined overlay range surfaces the same pencil/trash pill
  // the Content Studio clip preview shows (PLAT-10045) and opens the shared
  // `CompositionQuickEditPopover`. Wiring mirrors `ClipTranscriptBridge` but
  // reads the global stores instead of the clip adapter. Selection-driven
  // brightening stays pill-free (handled inside `Transcript`).
  const [openMarker, setOpenMarker] = useState<{
    marker: TimelineMarker;
    anchor: HTMLElement;
  } | null>(null);

  const extraElementData =
    (videoCreator.currentVideo?.extraElementData as
      | Record<string, unknown>
      | undefined) ?? null;

  // Drive the popover's language from the transcript view's own per-view
  // selection (URL `?lang`), NOT the editor's global karaoke caption config.
  // The editor config can be set to a translation by the captions tab or the
  // rendered-video state while the transcript is still showing Original —
  // reading it here made the popover render "(European Spanish) / Not yet
  // translated" even under en-us/Original. `null` (no `?lang`) = Original.
  const activeLangForPopover = selectedLanguage ?? ORIGINAL_LANGUAGE;

  const activeLanguageDisplay = useMemo<string | null>(() => {
    if (isOriginalLang(activeLangForPopover)) return null;
    return formatLanguageCaption(activeLangForPopover);
  }, [activeLangForPopover]);

  // Include the live `translations.get(lang)` reference so the memo
  // invalidates after a cache swap (see ClipTranscriptBridge for rationale).
  const activeLangTranslationData =
    translationStore.translations.get(activeLangForPopover) ?? null;
  const compositionPhrases = useMemo(
    () =>
      translationStore.getCompositionPhrasesByNormalizedSource(
        activeLangForPopover,
      ),
    [translationStore, activeLangForPopover, activeLangTranslationData],
  );

  const refreshSource = useCallback(() => {
    videoSourceStore.refreshSource();
  }, [videoSourceStore]);

  // PLAT-10935 — AI text generation for this surface. Same hook and same
  // callbacks the two clip-preview entry points build, so a graphic behaves
  // identically wherever it is edited (E1). Creator Studio's surface-specific
  // answers (story transcript, no `clipId`) live in one place, shared with the
  // composition settings panel (PLAT-10920).
  const { generation: aiTextGeneration, transcriptElements: aiTextTranscript } =
    useCreatorStudioAITextGeneration('creator-studio-transcript');

  const retranslateCompositionDispatch: RetranslateCompositionDispatch =
    useCallback(
      async ({ force, compositionElementId, sourceTexts }) => {
        // Read canonical (non-windowed) elements from the editor, not the
        // windowed preview source — see `getTranslatableSourceElements`.
        const { compositionTargets } = extractTranslatableElements({
          videoSourceElements: getTranslatableSourceElements(editor),
          extraElementData,
          targetLanguage: activeLangForPopover,
        });
        const result = await translationStore.retranslateCompositionPhrases({
          lang: activeLangForPopover,
          compositionTargets,
          force,
          // PLAT-10875: the active workspace's showcase, so the backend honors
          // this workspace's TM even if the story is in several.
          showcaseId: videoCreator.organization?.id,
          ...routeRetranslateTargetId(compositionElementId, compositionTargets),
          ...(sourceTexts ? { sourceTexts } : {}),
        });
        videoSourceStore.refreshSource();
        return result;
      },
      [
        editor,
        videoSourceStore,
        extraElementData,
        activeLangForPopover,
        translationStore,
        videoCreator,
      ],
    );

  const {
    upsert: upsertCompositionPhraseDispatch,
    revert: removeCompositionPhrasePinDispatch,
  } = useCompositionPinDispatchers({
    compositionElementId: openMarker?.marker.id ?? null,
    compositionPhrases,
    lang: activeLangForPopover,
    translationStore,
    editorDisplayName: userIdentity.currentEditor,
    onAfterCommit: refreshSource,
  });

  // Quick-edit fields for an arbitrary marker — shared by the open popover
  // (`popoverFields`) and the transcript hover pill so both gate the Edit
  // affordance on the same "has editable fields" rule (PLAT-10120). Logic
  // lives in the shared `computeMarkerQuickEditFields` util.
  const computeMarkerFields = useCallback(
    (marker: TimelineMarker) => {
      const topLevel = (
        videoSourceStore.source as {
          elements?: ReadonlyArray<Record<string, unknown>>;
        }
      )?.elements;
      return computeMarkerQuickEditFields(topLevel, extraElementData, marker);
    },
    [videoSourceStore.source, extraElementData],
  );

  const popoverFields = useMemo(
    () => (openMarker ? computeMarkerFields(openMarker.marker) : []),
    [openMarker, computeMarkerFields],
  );

  const handleMarkerClick = useCallback(
    (marker: TimelineMarker, anchor: HTMLElement) => {
      // Single-popover policy — clicking the same marker closes, a different
      // one swaps the anchor.
      let opened = false;
      setOpenMarker(prev => {
        if (prev && prev.marker.id === marker.id) return null;
        opened = true;
        return { marker, anchor };
      });
      // Seek the player to the overlay's midpoint so the rendered overlay is
      // visible on the canvas while editing.
      if (opened) {
        const midpoint = (marker.time + marker.endTime) / 2;
        if (Number.isFinite(midpoint)) {
          void videoCreator.setTime(midpoint).catch((error: unknown) => {
            logger.warn('Failed to seek to composition midpoint', {
              targetId: marker.id,
              midpoint,
              ...extractErrorInfo(error),
            });
          });
        }
      }
    },
    [videoCreator],
  );

  const closePopover = useCallback(() => setOpenMarker(null), []);

  useCompositionQuickEditOpenedTracking({
    isOpen: !!openMarker,
    targetId: openMarker?.marker.id ?? null,
    compositionType: openMarker?.marker.type ?? null,
    fields: popoverFields,
    storyId: videoCreator.storyId ?? null,
    surface: 'creator-studio-transcript',
  });

  const handleSaved = useCallback(
    (summary: {
      originalRowsCommitted: number;
      translatedRowsCommitted: number;
    }) => {
      if (!openMarker) return;
      if (summary.originalRowsCommitted === 0) return;
      const storyId = videoCreator.storyId ?? undefined;
      const language = isOriginalLang(activeLangForPopover)
        ? '<original>'
        : activeLangForPopover;
      for (const f of popoverFields) {
        trackCompositionTextEdited({
          story_id: storyId,
          composition_element_id: openMarker.marker.id,
          composition_type: openMarker.marker.type,
          surface: 'creator-studio-transcript',
          language,
          source_text_length: f.originalValue.length,
        });
      }
    },
    [openMarker, popoverFields, videoCreator, activeLangForPopover],
  );

  const handleOpenOverlay = useCallback(
    (overlayId: string, anchor: HTMLElement) => {
      const marker = timelineMarkers.overlays.find(m => m.id === overlayId);
      if (!marker) return;
      handleMarkerClick(marker, anchor);
    },
    [timelineMarkers.overlays, handleMarkerClick],
  );

  const handleDeleteOverlay = useCallback(
    (overlayId: string) => {
      try {
        // Shared delete-an-overlay sequence (command path + repaint + meta
        // cleanup + post-delete flush) — see `removeOverlayAndPersist`
        // (PLAT-10191). Background autosave re-exports the live editor.
        const removed = removeOverlayAndPersist(overlayId, {
          editor: editorStore.editor,
          video: videoCreator.currentVideo ?? null,
          refreshSource,
          persist: () =>
            videoCreator.saveStoryAndVideo(false, true, false, true),
          logger,
        });

        logger.info(
          'Timeline overlay removed via transcript hover action menu',
          { targetId: overlayId, removed },
        );
      } catch (error) {
        logger.error('Timeline overlay removal failed', {
          targetId: overlayId,
          ...extractErrorInfo(error),
        });
      }
    },
    [editorStore, refreshSource, videoCreator],
  );

  // Commit a re-anchored overlay range. `time`/`duration` are resolved by the
  // transcript from the boundary words; the marker re-derives off `tracksMap`
  // so the underline + handles follow.
  const handleAdjustOverlayRange = useCallback(
    (
      overlayId: string,
      {
        time,
        duration,
        anchorStartInitialIndex,
        anchorEndInitialIndex,
      }: {
        time: number;
        duration: number;
        anchorStartInitialIndex?: number;
        anchorEndInitialIndex?: number;
      },
    ) => {
      // Keep the overlay's transcript anchor in sync with the dragged range,
      // else the anchor-derived underline (findOverlayRanges) snaps back to
      // the old words on the next render.
      //
      // PLAT-10921 — BEFORE the command, not after. `commandUpdateElement` runs
      // a placement reconcile on its way out; with the anchor still pointing at
      // the old words, that reconcile re-derives from them and drags a pinned
      // `selection` overlay straight back.
      if (anchorStartInitialIndex != null && anchorEndInitialIndex != null) {
        upsertElementMetaObject(videoCreator.currentVideo, overlayId, {
          transcriptAnchor: {
            startInitialIndex: anchorStartInitialIndex,
            endInitialIndex: anchorEndInitialIndex,
          },
        });
      } else {
        // The dragged range crosses a reordered section boundary, so it cannot
        // be word-bound (`clampAnchorToSelectionRun`). Drop the anchor rather
        // than leave the old one — that is precisely the snap-back the comment
        // above warns about. With no anchor the graphic binds to the time window
        // it was just given, which is the correct binding once playback order
        // diverges from source order.
        deleteElementMetaKeys(videoCreator.currentVideo, overlayId, [
          'transcriptAnchor',
        ]);
      }
      // PLAT-10921 — the transcript view is the second place a creator changes
      // a timeline edge, so it records overrides exactly like a timeline drag.
      // The anchor rewrite above already IS the `selection` Rebind, which is
      // why no transcript is passed: it would recompute the same anchor from
      // the range the transcript just resolved from those very words.
      const previous = readElementTimelineRange(editorStore.editor, overlayId);
      if (previous) {
        applyPlacementEdgeOverrides({
          editor: editorStore.editor,
          video: videoCreator.currentVideo,
          elementId: overlayId,
          previous,
          next: { time, duration },
        });
      }

      const ok = editorStore.editor.commandUpdateElement(overlayId, {
        time,
        duration,
      });
      if (ok) {
        // Repaint immediately — the command's markDirty sync is debounced
        // ~300ms (CLAUDE.md Command Pattern). Mirrors the clip-preview surface.
        refreshSource();
      }
    },
    [editorStore, refreshSource, videoCreator],
  );

  const handleCommitOverlayTiming = useCallback(
    (next: { time: number; duration: number }) => {
      if (!openMarker) return;
      const elementId = openMarker.marker.id;
      // PLAT-10921 — same recording as the drag path, and likewise BEFORE the
      // command so its reconcile sees the rebound anchor. This one DOES pass the
      // transcript: the quick-edit popover commits raw times with no words
      // involved, so a `selection` graphic's Rebind has to derive the new anchor
      // here or the next reconcile would drag it back to the old passage. The
      // thunk means only a `selection` graphic ever pays for building it.
      const previous = readElementTimelineRange(editorStore.editor, elementId);
      if (previous) {
        applyPlacementEdgeOverrides({
          editor: editorStore.editor,
          video: videoCreator.currentVideo,
          elementId,
          previous,
          next,
          transcript: () => editorStore.editor.getFullTranscript(),
        });
      }
      const ok = editorStore.editor.commandUpdateElement(elementId, next);
      if (ok) refreshSource();
    },
    [editorStore, openMarker, refreshSource, videoCreator],
  );

  // PLAT-10105 — "Add Overlay Text & Graphic" from a transcript selection.
  // Same logic as the clip-preview surface (`ClipTranscriptBridge`): the
  // shared Transcript resolves the selection to a timeline range and hands it
  // here; this opens the filtered overlay picker and inserts the chosen
  // overlay bound to that range. Creator Studio renders against the real
  // stores already, so the picker reads the org's overlay templates directly.
  const [overlaySelection, setOverlaySelection] =
    useState<OverlayTranscriptRange | null>(null);

  const handleAddOverlayGraphicRequested = useCallback(
    (range: OverlayTranscriptRange) => {
      setOverlaySelection(range);
    },
    [],
  );

  const handleSelectOverlay = useCallback(
    async (template: CompositionTemplate) => {
      const range = overlaySelection;
      if (!range) return;
      setOverlaySelection(null);

      // PLAT-10213 — shared add-overlay sequence (analytics + insert bound to
      // the highlighted range + repaint + error toast). Same helper the
      // clip-preview surface uses (`ClipTranscriptBridge`); only the `surface`
      // tag and the video-like object differ. See `addOverlayFromTranscript`.
      await addOverlayFromTranscript({
        template,
        range,
        surface: 'creator-studio-transcript',
        editorStore,
        video: {
          storyId: videoCreator.storyId,
          duration: videoCreator.duration,
          getCurrentVideo: () => videoCreator.currentVideo,
          getCurrentAspectRatio: () => videoCreator.getCurrentAspectRatio(),
          organization: videoCreator.organization,
          story: videoCreator.story,
          convertToVh: (v: string) => videoCreator.textProducer.convertToVh(v),
          setActiveElements: (id: string) => videoCreator.setActiveElements(id),
          setTime: (time: number) => videoCreator.setTime(time, true),
        },
        refreshSource,
        onError: message => {
          videoCreator.showToast({
            state: 'error',
            message,
          });
        },
        // PLAT-10935 DS5 — the same automatic first generation clip preview
        // fires on insert. Without it an identical card would arrive generated
        // in one studio and blank in the other.
        onAITextControlsCreated: aiTextGeneration.generateForNewComposition,
        logger,
      });
    },
    [
      overlaySelection,
      editorStore,
      videoCreator,
      refreshSource,
      aiTextGeneration,
    ],
  );

  const renderHighlightPopover = useCallback(
    (info: HighlightHoverInfo) => {
      if (!info.overlayId) return null;
      const overlayId = info.overlayId;
      const marker = timelineMarkers.overlays.find(m => m.id === overlayId);
      if (!marker) return null;
      const editLabel = `Edit ${formatHeaderLabelFromMarker(marker)}`;
      // Gate Edit on the same rule as the canvas action menu: an overlay with no
      // editable fields has nothing to open, so disable Edit instead of letting
      // a click silently no-op (PLAT-10120 follow-up).
      const editDisabled = computeMarkerFields(marker).length === 0;
      // Anchor BELOW the underline (word's bottom edge) so the pill never
      // covers the line being edited. `info.anchorRect.bottom` is the
      // line-box bottom; +26 clears the underline + sits in the gap. But when
      // the overlay is the last sentence (near the bottom of the scroll area),
      // "below" pushes the pill past the panel and its Delete button becomes
      // unreachable — flip ABOVE the underline in that case (PLAT-10961). The
      // pill is center-anchored via translate(-50%,-50%) and ~48px tall, so we
      // reserve half its height below the center point. Measure against the
      // transcript scroll container (found via the hovered span so we hit the
      // right instance — see the duplicate-id pitfall) rather than the whole
      // window, since content sits below the panel.
      const PILL_HALF_HEIGHT = 24;
      const scrollBottom =
        info.anchorElement
          .closest<HTMLElement>('#transcript-scrollable-container')
          ?.getBoundingClientRect().bottom ?? window.innerHeight;
      const belowCenter = info.anchorRect.bottom + 26;
      const placeAbove = belowCenter + PILL_HALF_HEIGHT > scrollBottom;
      const top = placeAbove ? info.anchorRect.top - 24 : belowCenter;
      return createPortal(
        <TranscriptHoverMenuPosition
          data-testid="transcript-highlight-action-popover"
          style={{
            left: `${info.anchorRect.left + info.anchorRect.width / 2}px`,
            top: `${top}px`,
          }}
          onMouseEnter={info.onMouseEnter}
          onMouseLeave={info.onMouseLeave}
        >
          <CompositionCanvasActionMenu
            ariaLabel={editLabel}
            editTooltip={editLabel}
            editDisabled={editDisabled}
            editDisabledReason="This overlay has no editable text"
            onEdit={anchor =>
              handleOpenOverlay(overlayId, anchor ?? info.anchorElement)
            }
            onAdjustRange={info.onAdjustRange}
            onDelete={() => handleDeleteOverlay(overlayId)}
          />
        </TranscriptHoverMenuPosition>,
        document.body,
      );
    },
    [
      timelineMarkers.overlays,
      handleOpenOverlay,
      handleDeleteOverlay,
      computeMarkerFields,
    ],
  );

  // PLAT-11877 — the player's settings above the picture: Voice / Music for
  // the language being read once it has a dub.
  const playerSettingsSlot = usePlayerSettingsSlot();
  const dubAudio =
    dubGeneration.composition.onAudioModeChange &&
    !!selectedLanguage &&
    dubGeneration.composition.dubbedLanguages.includes(selectedLanguage)
      ? {
          audioMode: dubGeneration.composition.audioMode,
          onAudioModeChange: dubGeneration.composition.onAudioModeChange,
          busy: dubGeneration.composition.audioModeBusy,
          musicMode: dubGeneration.musicController.musicMode,
          onMusicModeChange: dubGeneration.musicController.setMusicMode,
          musicAvailable:
            dubGeneration.musicController.hasStems &&
            !dubGeneration.musicController.noMusic,
          storyId: videoCreator.storyId ?? undefined,
          language: dubGeneration.composition.controlLanguage,
        }
      : undefined;
  // No Captions picker here (PLAT-11877): the Captions sidebar is where
  // Creator Studio turns captions on and picks their style, and a second
  // control for the same thing beside the picture only competed with it.
  const playerAudioPickers =
    playerSettingsSlot && dubAudio
      ? createPortal(
          <PlayerAudioPickers
            audio={dubAudio}
            menuZIndex={TOP_LAYER_Z.contextMenu}
          />,
          playerSettingsSlot,
        )
      : null;

  const languageToggle = (
    <TranslationToggle
      selectedLanguage={selectedLanguage}
      onLanguageChange={setSelectedLanguage}
      onRequestGenerate={generateModal.onRequestGenerate}
      generateDisabled={generateModal.isOpen}
      supportedLanguages={supportedLanguages}
      surface="creator-studio-transcript"
      // PLAT-11877 — the review entries (pencil, microphone, row
      // actions), as the clip preview has them.
      onRequestReview={openReview}
      onRequestRegenerateDub={dubGeneration.onRequestRegenerateDub}
      onRequestRemoveDub={
        dubGeneration.composition.onAudioModeChange
          ? removeDub.request
          : undefined
      }
      onRemoveDubForLanguage={
        dubGeneration.composition.onAudioModeChange
          ? removeDub.remove
          : undefined
      }
      // `onRequestDub` is deliberately not passed: generating a dub from
      // scratch stays on the Transcript & Speakers tab. PLAT-11555 wires
      // the narrower `onRequestFirstDub` instead — the button alone, for
      // a language the story has no dub in, dubbing the timeline's own
      // lines.
      onRequestFirstDub={dubGeneration.onRequestFirstDub}
      // PLAT-11532 — the timeline-length cap, so the button above can't
      // start a run that would time out part-way and keep nothing.
      dubUnavailableNote={dubGeneration.dubUnavailableNote}
      audioMode={dubGeneration.composition.audioMode}
      appliedDubLanguage={dubGeneration.composition.appliedDubLanguage}
      onAudioModeChange={dubGeneration.composition.onAudioModeChange}
      audioModeBusy={dubGeneration.composition.audioModeBusy}
      dubbedLanguages={dubGeneration.composition.dubbedLanguages}
      // PLAT-11877 — Voice and Music sit beside the player (the Stage
      // bar's `PlayerAudioPickers`), not in this menu.
      audioControlsInPlayer
      // PLAT-11899 — the timeline's Dub Level, saved on release like the
      // Voice toggle. Not on Original: no dub is heard there.
      // PLAT-11927 — on Both, the original's level above it.
      dubLevelControl={
        dubGeneration.composition.onVoiceVolumeChange &&
        dubGeneration.composition.audioMode !== 'original' ? (
          <>
            {dubGeneration.composition.audioMode === 'both' &&
              dubGeneration.composition.onOriginalVolumeChange && (
                <DubLevelRow
                  label="Original Level"
                  level={dubGeneration.composition.originalVolume}
                  onChange={dubGeneration.composition.onOriginalVolumeChange}
                />
              )}
            <DubLevelRow
              level={dubGeneration.composition.voiceVolume}
              onChange={dubGeneration.composition.onVoiceVolumeChange}
            />
          </>
        ) : undefined
      }
      // PLAT-11555 — the same gap warning the clip preview shows, for
      // this screen. Renders nothing at all when the timeline is fully
      // dubbed, so it cannot become wallpaper people stop reading.
      coverageNotice={({ close }) => (
        <DubCoverageNotice
          coverage={dubGeneration.coverage}
          tooltipId={LANGUAGE_MENU_HELP_TOOLTIP_ID}
          // PLAT-11585 — close the menu first so it does not sit on top
          // of the progress modal this opens.
          onFillGaps={() => {
            close();
            dubGeneration.onFillGaps();
          }}
          // Same close-first reason: the re-dub opens the same progress
          // modal the fill does.
          onReDubStale={() => {
            close();
            dubGeneration.onReDubStale();
          }}
          busy={dubGeneration.busy}
          // Same cap on the repair run: the notice goes on naming the
          // gap, with its action disabled and the reason under it.
          fillGapsDisabledNote={dubGeneration.dubUnavailableNote?.tooltip}
          // On Original the published file keeps the original recording,
          // so a figure about the dub describes a file nobody will get.
          audioMode={dubGeneration.composition.audioMode}
          // "reel", not "clip": this sum covers the whole story timeline,
          // and there is no clip on this screen to name.
          subjectNoun="reel"
          noDubLanguageLabel={
            dubGeneration.composition.controlLanguage
              ? (supportedLanguages.find(
                  language =>
                    language.value ===
                    dubGeneration.composition.controlLanguage,
                )?.caption ??
                formatLanguageCaption(
                  dubGeneration.composition.controlLanguage,
                ))
              : undefined
          }
        />
      )}
    />
  );

  return (
    <Panel
      currentWidth={transcriptWidth}
      maxWidth={transcriptMaxWidth}
      minWidth={transcriptMinWidth}
      $animate={!isDragging}
    >
      <TranscriptOverflowExtrasProvider>
        <Transcript
          surface="creator_studio"
          videoCreator={videoCreator}
          onOpenClip={handleOpenSavedClip}
          saveService={saveService}
          savePullQuoteService={savePullQuoteService}
          clipSaveService={clipSaveService}
          mediaService={mediaService}
          hideAddPhoto={!creatorStudioTranscriptAddPhoto}
          hideAddOverlayGraphic={
            !enableTranscriptOverlayAdd || !enableCompositionCanvasQuickEdit
          }
          onAddOverlayGraphicRequested={handleAddOverlayGraphicRequested}
          clipTimelineMarkers={overlayOnlyMarkers}
          renderHighlightPopover={renderHighlightPopover}
          onAdjustOverlayRange={
            enableTranscriptOverlayRangeAdjust
              ? handleAdjustOverlayRange
              : undefined
          }
          language={selectedLanguage}
          enableInlineSpanReplace={enableTranslationReview}
          enableChangedHighlight={enableTranslationReview}
          onCaptionsViewOpen={followUrlLanguage}
          headerExtras={languageToggle}
          // Reads the surface's own editor via context — see the component's note
          // on why the hook must not be lifted to the host.
          belowHeader={<CaptionStalenessNotice />}
        />
      </TranscriptOverflowExtrasProvider>
      {generateModal.isOpen && (
        <TranslationGenerateModal
          {...generateModal.modalProps}
          onSuccess={language => {
            setSelectedLanguage(language);
            generateModal.close();
          }}
          // PLAT-11877 — the "ready" toast opens the review for the new
          // language, as the clip preview's does.
          successAction={
            enableTranslationReview
              ? language => ({
                  label: 'Review Translation',
                  onClick: () => openReview(language, 'translation'),
                })
              : undefined
          }
          supportedLanguages={supportedLanguages}
        />
      )}
      {removeDub.pendingLanguage && (
        <ConfirmModal
          closeModal={removeDub.cancel}
          onCancel={removeDub.cancel}
          onConfirm={removeDub.confirm}
          title={`Remove the story's ${captionForLanguage(
            removeDub.pendingLanguage,
          )} dub?`}
          confirmText="Remove dub"
          cancelText="Cancel"
        />
      )}
      {enableTranslationReview && reviewOpen && selectedLanguage && (
        <CreatorStudioTranslationReview
          language={selectedLanguage}
          mode={reviewMode}
          supportedLanguages={supportedLanguages}
          dubGeneration={dubGeneration}
          dubEntryEnabled={dubEntryEnabled}
          onClose={() => setReviewOpen(false)}
        />
      )}
      {dubGeneration.modalProps && (
        <DubGenerateModal
          {...dubGeneration.modalProps}
          supportedLanguages={supportedLanguages}
        />
      )}
      <DraggableCore
        onDrag={e => {
          handleMouseMove(e as unknown as MouseEvent);
        }}
        onStop={handleMouseUp}
        onMouseDown={handleMouseDown}
      >
        <ResizeBorderVertical
          onMouseUp={handleMouseUp}
          isDragging={isDragging}
          position="right"
        />
      </DraggableCore>
      {openMarker &&
        popoverFields.length > 0 &&
        createPortal(
          <CompositionQuickEditPopover
            // PLAT-10935 DS4 — remount per graphic. `handleMarkerClick` SWAPS
            // the open marker without closing, and the rows below are keyed by
            // control id — two Smart Graphics cards both key on `hook`, so
            // without this React reuses the AI text control and carries its
            // base-text scope, typed instructions and expanded state onto the
            // next graphic.
            key={openMarker.marker.id}
            // Top-level text overlay (empty path) writes to the resolved text
            // element id; composition placements walk from `marker.id`.
            targetId={resolveQuickEditTargetId(
              popoverFields,
              openMarker.marker.id,
            )}
            headerLabel={formatHeaderLabelFromMarker(openMarker.marker)}
            activeLanguageLabel={activeLanguageDisplay}
            anchorElement={openMarker.anchor}
            fields={popoverFields}
            editorStore={editorStore}
            activeLanguage={activeLangForPopover}
            compositionPhrases={compositionPhrases}
            retranslateCompositionPhrases={retranslateCompositionDispatch}
            upsertCompositionPhraseTranslation={upsertCompositionPhraseDispatch}
            removeCompositionPhrasePin={removeCompositionPhrasePinDispatch}
            sourceLanguage={transcriptionStore.language}
            storyId={videoCreator.storyId ?? null}
            compositionType={openMarker.marker.type}
            surface="creator-studio-transcript"
            timing={{
              time: openMarker.marker.time,
              endTime: openMarker.marker.endTime,
            }}
            // PLAT-10935 — the AI text control resolves its base text from the
            // same marker + story transcript the underline uses. Shared with
            // the canvas Edit pill (`CompositionCanvasActionsLayer`) so the two
            // Creator Studio entry points can't drift.
            aiTextContext={buildAITextPopoverContext({
              aiTextGeneration,
              transcriptElements: aiTextTranscript,
              overlay: openMarker.marker,
            })}
            onCommitTiming={handleCommitOverlayTiming}
            onSaved={handleSaved}
            onClose={closePopover}
          />,
          document.body,
        )}
      {/* PLAT-10105 — overlay picker (same surface as clip preview). The Modal
          itself portals to document.body. Wrapped in CompositionProvider so it
          reads the org's overlay templates. */}
      {overlaySelection && (
        <CompositionProvider>
          <OverlayGraphicPickerModal
            onSelect={handleSelectOverlay}
            onClose={() => setOverlaySelection(null)}
            currentAspectRatio={videoCreator.getCurrentAspectRatio()}
          />
        </CompositionProvider>
      )}
      {playerAudioPickers}
    </Panel>
  );
});

/**
 * PLAT-10120 — wrapper for the transcript-hover canvas-style action pill.
 * (left, top) inline style is the desired CENTER anchor point;
 * `translate(-50%, -50%)` self-centers the pill on it. Mirrors the
 * positioning convention used by `ClipTranscriptBridge`'s hover pill.
 */
const TranscriptHoverMenuPosition = styled.div`
  position: fixed;
  pointer-events: none;
  transform: translate(-50%, -50%);
  /* Must sit ABOVE the shared OverlayRangeHandles layer
     (CLIP_PREVIEW_Z.overlayRangeHandles = 1000011), which portals to
     document.body at a seven-figure z to clear the clip-preview modal. The
     old six-figure 100001 left the range-adjust handles painting on top of
     this pill in Creator Studio. Reuse the clip-preview hover-pill token so
     both surfaces' pills stay in lockstep above the handles. */
  z-index: ${CLIP_PREVIEW_Z.transcriptHoverPill};

  & > * {
    pointer-events: auto;
  }
`;

const Panel = styled.div<{
  currentWidth: string;
  minWidth: number;
  maxWidth: number;
  $animate: boolean;
}>`
  position: relative;
  padding: 0px 5px 5px;
  width: ${props => props.currentWidth || '380px'};
  /* Programmatic width changes (a right panel opening) glide in step with
     ExpandablePanel; a drag must track the pointer, so no transition then. */
  transition: ${props =>
    props.$animate ? 'width var(--transition-sidebar)' : 'none'};
  min-width: ${props => props.minWidth || '190px'};
  max-width: ${props => props.maxWidth || '760px'};
  display: flex;
  /* A draggable edge keeps a resting hairline so the handle is findable;
     never full --border ("Structural Lines" in docs/design-system.md). */
  border-right: 1px solid var(--border-subtle);
`;
